import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useNotifications } from '@/hooks/useNotifications';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SecondaryButton } from '@/components/ui/button-variants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Bell, 
  ArrowLeft, 
  MoreVertical, 
  CheckCheck, 
  Trash2,
  Heart,
  MessageCircle,
  Users,
  Calendar,
  AlertTriangle,
  Mail,
  Check
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import messagesHeroBg from '@/assets/messages-hero-bg.jpg';

const Notifications = () => {
  const { 
    notifications, 
    unreadCount, 
    loading, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification 
  } = useNotifications();
  
  // Set document title and meta description for SEO
  useEffect(() => {
    document.title = 'Notifiche - Rimani Aggiornato | AstroPet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Gestisci le tue notifiche su AstroPet. Ricevi aggiornamenti su like, commenti, richieste di adozione, messaggi e attività della community pet. Non perdere mai un momento importante.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Gestisci le tue notifiche su AstroPet. Ricevi aggiornamenti su like, commenti, richieste di adozione, messaggi e attività della community pet. Non perdere mai un momento importante.';
      document.head.appendChild(meta);
    }
    
    // Set canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://astropet.it/notifications');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://astropet.it/notifications';
      document.head.appendChild(link);
    }
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'like': return Heart;
      case 'comment': return MessageCircle;
      case 'follow': return Users;
      case 'adoption_request': return Heart;
      case 'sos_help': return AlertTriangle;
      case 'event_join': return Calendar;
      case 'message': return Mail;
      default: return Bell;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'like': return 'text-red-500';
      case 'comment': return 'text-blue-500';
      case 'follow': return 'text-green-500';
      case 'adoption_request': return 'text-purple-500';
      case 'sos_help': return 'text-orange-500';
      case 'event_join': return 'text-indigo-500';
      case 'message': return 'text-cyan-500';
      default: return 'text-muted-foreground';
    }
  };

  const handleNotificationClick = async (notification: any) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    const data = notification.data;
    if (data?.post_id) {
      // Navigate to post
      window.location.href = `/app/post/${data.post_id}`;
    } else if (data?.follower_id && notification.type === 'follow') {
      // Navigate to user profile (you'll need to get username)
      // For now, just mark as read
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4 pb-20 lg:pb-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/app">
            <Button variant="ghost" size="sm" className="text-white hover:text-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-white">Notifiche</h1>
        </div>

        <Card className="glass border-white/20">
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-start space-x-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                  <Skeleton className="h-8 w-8" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${messagesHeroBg})` }}
    >
      <div className="max-w-2xl mx-auto p-4 pb-20 lg:pb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/app">
            <Button variant="ghost" size="sm" className="text-white hover:text-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-white">Notifiche</h1>
          {unreadCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {unreadCount} nuove
            </Badge>
          )}
        </div>

        {unreadCount > 0 && (
          <SecondaryButton onClick={markAllAsRead} size="sm">
            <CheckCheck className="w-4 h-4 mr-2" />
            Segna tutto come letto
          </SecondaryButton>
        )}
      </div>

      {/* Notifications */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Le tue notifiche
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {notifications.length === 0 ? (
            <div className="text-center py-16">
              <Bell className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Nessuna notifica</h3>
              <p className="text-muted-foreground">
                Ti avviseremo quando ci saranno aggiornamenti sui tuoi post e attività.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {notifications.map((notification) => {
                const IconComponent = getNotificationIcon(notification.type);
                const iconColor = getNotificationColor(notification.type);
                const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
                  addSuffix: true,
                  locale: it
                });

                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-white/5 transition-smooth cursor-pointer ${
                      !notification.is_read ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-4">
                      {/* Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center ${iconColor}`}>
                        <IconComponent className="w-5 h-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${notification.is_read ? 'text-muted-foreground' : 'text-white'}`}>
                              {notification.title}
                            </p>
                            {notification.message && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {notification.message}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              {timeAgo}
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center space-x-2">
                            {!notification.is_read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                }}
                                className="h-8 w-8 p-0 text-primary hover:text-primary-glow"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-muted-foreground hover:text-white"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="glass border-white/20">
                                {!notification.is_read && (
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      markAsRead(notification.id);
                                    }}
                                  >
                                    <Check className="w-4 h-4 mr-2" />
                                    Segna come letto
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNotification(notification.id);
                                  }}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Notifications;